import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, RefreshCw, Search } from 'lucide-react';

import PageHeader from '@/components/PageHeader';
import ManagerAuditPanel from '@/components/ManagerAuditPanel';
import SalesSafeCard from '@/components/SalesSafeCard';
import {
  createManager,
  listManagers,
  type ManagerListItem,
  type ManagerRoleFilter,
} from '@/lib/operatorApi';
import { formatKop } from '@/lib/format';

const ROLE_FILTERS: Array<{ value: ManagerRoleFilter; label: string }> = [
  { value: 'all', label: 'Все' },
  { value: 'director', label: 'Руководители' },
  { value: 'head', label: 'Главные' },
  { value: 'manager', label: 'Обычные' },
];

type Tab = 'list' | 'audit';

export default function Managers() {
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get('tab') === 'audit' ? 'audit' : 'list';
  const setTab = (next: Tab) =>
    setParams(next === 'audit' ? { tab: 'audit' } : {}, { replace: true });

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', code: '', commissionBps: '20' });
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setBusy(true);
      await createManager({
        email: form.email.trim(),
        name: form.name.trim(),
        code: form.code.trim(),
        commissionBps: Math.round(Number(form.commissionBps) * 100),
      });
      setShowCreate(false);
      setForm({ email: '', name: '', code: '', commissionBps: '20' });
      setReloadKey((k) => k + 1);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Ошибка');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Партнёры"
        title="Менеджеры"
        description="Реферальная программа: привлечённые заведения и комиссия с подписки. Главный менеджер видит все заведения, добавляет менеджеров, принимает наличные и продлевает сроки. Руководитель отдела продаж — ещё оборот компании, сейф, расходы и рейтинг."
        actions={
          <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={14} /> Добавить
          </button>
        }
      />

      <div className="border-b border-line bg-white px-4 md:px-8">
        <div className="flex gap-6">
          {(
            [
              ['list', 'Менеджеры'],
              ['audit', 'Аудит действий'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setTab(value)}
              className={
                '-mb-px border-b-2 py-3 text-[13px] font-medium transition ' +
                (tab === value ? 'border-ink text-ink' : 'border-transparent text-ink/55 hover:text-ink')
              }
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="page-pad">
        {tab === 'audit' ? (
          <ManagerAuditPanel />
        ) : (
          <div className="space-y-6">
            <SalesSafeCard />
            <ManagersList reloadKey={reloadKey} />
          </div>
        )}
      </div>

      {showCreate ? (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink/40 p-4">
          <form className="card w-full max-w-md p-6 space-y-3" onSubmit={onCreate}>
            <h2 className="font-semibold text-lg">Новый менеджер</h2>
            <div>
              <label className="label">Email</label>
              <input className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </div>
            <div>
              <label className="label">Имя</label>
              <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div>
              <label className="label">Код (для ?ref=)</label>
              <input className="input font-mono uppercase" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} required />
            </div>
            <div>
              <label className="label">Комиссия, %</label>
              <input className="input" type="number" min={0} max={100} step={0.1} value={form.commissionBps} onChange={(e) => setForm({ ...form, commissionBps: e.target.value })} />
            </div>
            <p className="text-[12px] text-ink/50">
              Роль главного менеджера назначается в карточке менеджера после создания.
            </p>
            <div className="flex gap-2 pt-2">
              <button type="button" className="btn-ghost flex-1" onClick={() => setShowCreate(false)}>Отмена</button>
              <button type="submit" className="btn-primary flex-1" disabled={busy}>{busy ? '…' : 'Создать'}</button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}

function ManagersList({ reloadKey }: { reloadKey: number }) {
  const [q, setQ] = useState('');
  const [role, setRole] = useState<ManagerRoleFilter>('all');
  const [items, setItems] = useState<ManagerListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    listManagers({ q: q.trim() || undefined, role, limit: 100 })
      .then((r) => {
        setItems(r.items);
        setTotal(r.total);
      })
      .finally(() => setLoading(false));
  }, [q, role]);

  useEffect(() => {
    const t = window.setTimeout(load, 200);
    return () => window.clearTimeout(t);
  }, [load, reloadKey]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap gap-1">
          {ROLE_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setRole(f.value)}
              className={
                'border px-3 py-1.5 text-[12px] font-medium transition ' +
                (role === f.value
                  ? 'border-ink bg-ink text-white'
                  : 'border-line bg-white text-ink/70 hover:border-ink/50')
              }
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-full md:w-80">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
            <input
              className="input !pl-9"
              placeholder="Поиск по имени, email, коду…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <button type="button" className="btn-ghost shrink-0" onClick={load} disabled={loading}>
            <RefreshCw size={13} /> Обновить
          </button>
        </div>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="text-left text-ink/50 border-b border-line">
              <th className="px-4 py-3">Менеджер</th>
              <th className="px-4 py-3">Роль</th>
              <th className="px-4 py-3">Код</th>
              <th className="px-4 py-3">Комиссия</th>
              <th className="px-4 py-3">Заведений</th>
              <th className="px-4 py-3">К выплате</th>
              <th className="px-4 py-3">Статус</th>
            </tr>
          </thead>
          <tbody>
            {items.map((m) => (
              <tr key={m.id} className="border-b border-line/60 last:border-0 hover:bg-paper/80">
                <td className="px-4 py-3">
                  <Link to={`/managers/${m.id}`} className="font-medium hover:text-orange">
                    {m.name}
                  </Link>
                  <div className="text-xs text-ink/45">{m.email}</div>
                  {m.createdByManager ? (
                    <div className="text-[11px] text-ink/40">добавил {m.createdByManager.name}</div>
                  ) : null}
                </td>
                <td className="px-4 py-3">
                  {m.role === 'SALES_DIRECTOR' ? (
                    <span className="badge-orange">руководитель</span>
                  ) : m.role === 'HEAD_MANAGER' ? (
                    <span className="badge-orange">главный</span>
                  ) : (
                    <span className="text-ink/55">менеджер</span>
                  )}
                </td>
                <td className="px-4 py-3 font-mono text-xs">{m.code}</td>
                <td className="px-4 py-3">{(m.commissionBps / 100).toLocaleString('ru-RU')}%</td>
                <td className="px-4 py-3">{m.establishments}</td>
                <td className="px-4 py-3 font-medium">{formatKop(m.pendingKopecks)}</td>
                <td className="px-4 py-3">
                  {m.deletedAt ? (
                    <span className="badge-red" title={`Удалён руководителем ${new Date(m.deletedAt).toLocaleDateString('ru-RU')}`}>
                      удалён
                    </span>
                  ) : m.status === 'blocked' ? (
                    <span className="badge-amber">заблокирован</span>
                  ) : (
                    <span className="text-ink/55">активен</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && !items.length ? (
          <div className="p-8 text-center text-ink/50 text-sm">Менеджеров пока нет</div>
        ) : null}
      </div>
      <div className="text-xs text-ink/45">Всего: {total}</div>
    </div>
  );
}
